<template>
  <div id="app">
    <!-- 使用 loading 状态控制加载动画 -->
    <transition name="fade">
      <div v-if="loading" class="app-loading-wrapper">
        <div class="app-loading-content">
          <div class="spinner">
            <svg viewBox="0 0 50 50" class="spinner-svg">
              <circle class="spinner-path" cx="25" cy="25" r="20" fill="none" stroke-width="5"></circle>
            </svg>
          </div>
          <!-- <p class="loading-text">{{ loadingText }}</p> -->
        </div>
      </div>
    </transition>

    <!-- 使用 keep-alive 包裹需要缓存的页面 -->
    <keep-alive>
      <router-view v-if="$route.meta.keepAlive" />
    </keep-alive>
    <!-- 不需要缓存的页面直接用 router-view 展示 -->
    <router-view v-if="!$route.meta.keepAlive" />
  </div>
</template>

<script>
import Vue from 'vue'
import * as api from '@/api/modules/api'
import { Toast } from 'vant';
// 引入英文语言包
import enUS from 'vant/es/locale/lang/en-US';
import zhCN from 'vant/es/locale/lang/zh-CN';
import { Locale } from 'vant';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import util from '@/libs/util.js'
import Cookies from 'js-cookie';
import VueCookie from 'vue-cookie'
import dayjs from 'dayjs'
import auth from '@/utils/auth' // 导入Token管理服务
import session from '@/utils/session' // 导入Session管理服务
import { update_user_info } from '@/views/login/api' // 导入用户信息更新API

Vue.use(Toast);
Vue.use(VueCookie)
export default {
  name: 'login',
  data() {
    return {
      isDataReady: false, // 数据是否准备好
      initTimeoutId: null, // 用于初始化超时保护的定时器ID
    }
  },

  computed: {
    ...mapState('tccc', {
      language: state => state.individualCenter.language,
      loading: state => state.app.loading,
      loadingText: state => state.app.loadingText,
      userInfoNeedsUpdate: state => state.app.userInfoNeedsUpdate,
      user: state => state.user
    }),
    // 添加 keepAliveList 计算属性
    keepAliveList() {
      return ['index'] // 直接指定需要缓存的组件名称
    }
  },

  created() {
    console.log('⏱️ [App] App组件created钩子开始执行:', new Date().toISOString());
    const startTime = performance.now();
    
    // 添加 session_key 检查和定时器
    this.checkAndStartSessionTimer();
    
    // 在 created 中就开始恢复数据
    // this.restoreUserData();
    
    // 设置初始化文本
    this.$store.dispatch('tccc/app/setLoadingText', '应用加载中...');
    
    // 设置一个超时保护，以防加载卡住
    this.initTimeoutId = setTimeout(() => {
      console.log('⏱️ [App] 初始化超时保护触发，强制完成初始化');
      this.$store.dispatch('tccc/app/setLoading', false);
    }, 5000); // 5秒超时保护
    
    // 监听路由变化
    this.$router.beforeEach((to, from, next) => {
      // 如果是初始路由加载，保持loading状态
      if (!from.name) {
        // 首次加载，保持loading状态
        console.log('⏱️ [App] 首次路由加载:', to.path);
      } else {
        // 不是首次加载，说明已经完成初始化
        console.log('⏱️ [App] 后续路由加载，完成初始化:', to.path);
        this.$store.dispatch('tccc/app/setLoading', false);
      }
      next();
    });
    
    console.log(`⏱️ [App] App组件created钩子执行完成, 耗时: ${(performance.now() - startTime).toFixed(2)}ms`);
  },

  mounted() {
    console.log('⏱️ [App] App组件mounted钩子开始执行:', new Date().toISOString());
    const startTime = performance.now();
    
    // 初始化Token服务 - 进入页面后确保Token可用
    // this.ensureTokenAvailable();
    
    // 在 mounted 中，如果数据已准备好，则执行其他异步任务
    if (this.isDataReady) {
      this.performAsyncTasks();
    }
    
    console.log(`⏱️ [App] App组件mounted钩子执行完成, 耗时: ${(performance.now() - startTime).toFixed(2)}ms`);
  },
  
  methods: {
    /**
     * 恢复用户数据 - 同步加载用户数据，确保组件渲染前用户数据可用
     */
    async restoreUserData() {
      try {
        // 尝试从localStorage中获取用户信息
        const userInfo = util.localStorage.get('userInfo');
        if (userInfo) {
          const parsedInfo = JSON.parse(userInfo);
          if (parsedInfo && parsedInfo.userid) {
            // 先从localStorage设置基本信息，确保userid可用
            await this.$store.dispatch('tccc/user/set', { 
              userid: parsedInfo.userid,
            }, { root: true });
            
            // 然后尝试从数据库加载完整信息
            await this.$store.dispatch('tccc/user/load', null, { root: true });
            
            // 设置client_id，避免接口调用失败
            const clientId = util.localStorage.get('client_id');
            if (clientId) {
              await this.$store.dispatch('tccc/client/set', { client_id: clientId }, { root: true });
            }
            
            console.log('用户数据恢复成功:', this.$store.state.tccc.user.info);
          }
        }
      } catch (error) {
        console.error('恢复用户数据失败:', error);
      } finally {
        // 无论成功失败，都标记数据已准备好，允许页面渲染
        this.isDataReady = true;
        this.$store.dispatch('tccc/app/setLoading', false);
      }
    },

    /**
     * 确保Token可用
     * @description 检查并确保Token有效，支持回退机制
     */
    async ensureTokenAvailable() {
      try {
        // 尝试初始化Token
        const success = await auth.initTokenService('App.ensureTokenAvailable');
        if (!success) {
          console.warn('Token初始化失败，尝试使用客户端配置重试');
          
          try {
            // 获取当前客户端配置
            const config = this.$clientConfig.getAuthConfig();
            
            if (config && config.appid && config.app_secret) {
              util.localStorage.set('appid', config.appid);
              util.localStorage.set('app_secret', config.app_secret);
              await auth.refreshToken();
            }
          } catch (error) {
            console.error('使用客户端配置获取Token失败', error);
          }
        }
      } catch (error) {
        console.error('确保Token可用失败:', error);
      }
    },
    
    /**
     * 执行异步任务
     * @description 页面加载后执行的低优先级任务
     */
    async performAsyncTasks() {
      // 延迟执行，让页面渲染先完成
      setTimeout(async () => {
        try {
          // 检查是否需要更新用户信息
          if (this.userInfoNeedsUpdate && this.user && this.user.info && this.user.info.userid) {
            this.$store.dispatch('tccc/app/setLoadingText', '正在同步用户信息...', { root: true });
            
            try {
              // 异步更新用户信息
              await update_user_info({
                ad_uid: '',
                client_id: util.localStorage.get('client_id'),
                data: {
                  user_id: this.user.info.userid,
                  username: this.user.info.full_name || '',
                  email: this.user.info.email || '',
                  mobile: this.user.info.mobile || ''
                }
              });
              
              // 更新完成，重置标志
              this.$store.dispatch('tccc/app/setUserInfoNeedsUpdate', false, { root: true });
            } catch (error) {
              console.error('用户信息更新失败', error);
              // 失败时不阻塞用户使用
            }
          }
        } catch (error) {
          console.error('异步任务执行失败', error);
        } finally {
          // 确保加载状态被关闭
          this.$store.dispatch('tccc/app/setLoading', false, { root: true });
        }
      }, 2000); // 延迟2秒执行，确保主要UI已渲染
    },

    /**
     * 检查session_key并启动定时器
     */
    async checkAndStartSessionTimer() {
      try {
        const user_id = util.localStorage.get('user_id') || util.cookies.get('userid');
        
        if (user_id) {
          console.log('App初始化: 检查session_key状态');
          
          // 检查是否有有效的session_key，使用提前2分钟更新策略
          if (!session.isSessionKeyValid(2)) {  // 修改为2分钟
            console.log('App初始化: session_key无效或即将过期，尝试刷新');
            try {
              await session.refreshSessionKey();
              console.log('App初始化: session_key刷新成功');
            } catch (error) {
              console.error('App初始化: session_key刷新失败', error);
            }
          } else {
            console.log('App初始化: session_key有效，无需刷新');
          }
          
          // 启动主动检查定时器，每3分钟检查一次
          session.startSessionCheckTimer(3 * 60 * 1000);
        } else {
          console.log('App初始化: 未找到用户ID，跳过session_key检查');
        }
      } catch (error) {
        console.error('检查session_key失败:', error);
      }
    }
  },
  
  watch: {
    language(newVal, oldVal) {
      if (newVal == 'zh-CN') {
        Locale.use('zh-CN', zhCN);
      } else {
        Locale.use('en-US', enUS);
      }
    },
  },
  
  beforeDestroy() {
    if (window.tokenRefreshTimer) {
      clearInterval(window.tokenRefreshTimer)
    }
    
    // 确保停止session_key检查定时器
    session.stopSessionCheckTimer();
  }
}
</script>
<style lang="scss">
@import "~@/assets/style/public-class.scss";
@import "~@/assets/style/yxt-public.scss";

#app {
  height: 100%;
  font-size: 14px;
  position: relative;
  // padding-bottom: constant(safe-area-inset-bottom);
  // padding-bottom: env(safe-area-inset-bottom);
}

/* 加载动画样式 */
.app-loading-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ffffff;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.app-loading-content {
  text-align: center;
}

.spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 20px;
}

.spinner-svg {
  animation: rotate 2s linear infinite;
  transform-origin: center center;
}

.spinner-path {
  stroke: #2997ff;
  stroke-dasharray: 150,200;
  stroke-dashoffset: -10;
  animation: dash 1.5s ease-in-out infinite;
  stroke-linecap: round;
}

.loading-text {
  font-size: 14px;
  color: #666;
  margin-top: 10px;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -124;
  }
}

/* 淡入淡出动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
